import { apiErrorSchema, ApiRequestError } from './api';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

export type ExportReport = 'overview' | 'pnl' | 'expenses' | 'company' | 'production';
export type ExportFormat = 'pdf' | 'xlsx';

export interface ExportParams {
  businessUnitId?: string;
  periodId?: string;
  year?: number;
}

/** Filename from Content-Disposition (RFC 5987 first, plain fallback). */
export function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      /* fall through */
    }
  }
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1] : fallback;
}

/**
 * The browser downloads what the backend generated — the report is never
 * recomputed client-side.
 */
export async function downloadExport(
  report: ExportReport,
  format: ExportFormat,
  params: ExportParams = {},
): Promise<void> {
  const query = new URLSearchParams({ format });
  if (params.businessUnitId) query.set('business_unit_id', params.businessUnitId);
  if (params.periodId) query.set('period_id', params.periodId);
  if (params.year != null) query.set('year', String(params.year));

  const response = await fetch(`${BASE_URL}/exports/${report}?${query}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    let code = 'export_failed';
    let message = `Экспорт не удался (${response.status})`;
    try {
      const parsed = apiErrorSchema.safeParse(await response.json());
      if (parsed.success) {
        code = parsed.data.error.code;
        message = parsed.data.error.message;
      }
    } catch {
      /* non-JSON error body */
    }
    throw new ApiRequestError(response.status, code, message);
  }

  const blob = await response.blob();
  const filename = filenameFromDisposition(
    response.headers.get('content-disposition'),
    `report.${format}`,
  );
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
